import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { ListStore } from '@list-store';
import { ConversationMessageView } from '@mark43/cad-api';
import { useCallback } from 'react';
import { UpdateBookmark } from '../../../common/broadcast-channel/types';
import {
    incrementBookmarkedMessageCount,
    decrementBookmarkedMessageCount,
} from '../../helpers/bookmark-helpers';
import { ConversationKey } from '../../models';
import { MessagingStore } from '../../store/messaging-store';

export const useUpdateBookmarkMessageHandler = (store: MessagingStore) => {
    const queryClient = useQueryClient();

    const updateMessageList = useCallback(
        ({
            message,
            queryClient,
            listType,
            conversationKey,
        }: {
            message: ConversationMessageView;
            queryClient: QueryClient;
            listType: 'conversations' | 'bookmarked-conversations';
            conversationKey: ConversationKey;
        }) => {
            const { participantIdentity } = conversationKey;

            if (participantIdentity === undefined) {
                throw new Error('Expected conversationKey participant identity missing');
            }

            const queryKey = [
                'conversation-messages',
                conversationKey.toString({
                    includeBookmarkedString: listType === 'bookmarked-conversations',
                }),
            ];
            const queryData = queryClient.getQueryData<{
                messageList: ListStore<ConversationMessageView>;
            }>(queryKey);

            const messageList = queryData?.messageList;

            switch (listType) {
                case 'conversations':
                    messageList?.upsertItems([message]);
                    break;
                case 'bookmarked-conversations':
                    if (message.isBookmarked) {
                        incrementBookmarkedMessageCount(store, message, participantIdentity);
                        messageList?.upsertItems([message]);
                    } else {
                        messageList?.removeItems([message.id]);
                        decrementBookmarkedMessageCount(store, message, participantIdentity);
                    }
                    break;
            }

            // no mutation allowed so we return a new constructed object
            // see here https://tanstack.com/query/v4/docs/react/guides/updates-from-mutation-responses#immutability
            if (messageList) {
                queryClient.setQueryData(queryKey, { messageList });
            }
        },
        [store]
    );

    const updateBookmarkMessageHandler = (updateBookmarkMessage: UpdateBookmark) => {
        const { message, conversationKeyObject } = updateBookmarkMessage.payload;

        const conversationKey = ConversationKey.fromObject(conversationKeyObject);

        // update conversation message list
        updateMessageList({
            message,
            queryClient,
            listType: 'conversations',
            conversationKey,
        });
        // update bookmarked conversation message list
        updateMessageList({
            message,
            queryClient,
            listType: 'bookmarked-conversations',
            conversationKey,
        });
    };

    return updateBookmarkMessageHandler;
};
